import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { UserModal } from "../../widgets/user"

/* Shared */
import { KINDS, stateOf, channelOf, ago, initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { plural } from "../../widgets/category/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list } from "../../api/User"


/* The orders of the list */
const SORTS = [
    { key: "new", text: "Новые" },
    { key: "recent", text: "Недавно записывались" },
    { key: "bookings", text: "Больше записей" },
    { key: "name", text: "А — Я" }
]

/* The empty filters */
const INITIAL = { search: "", kind: "all", state: "all", channel: "", sort: "new" }


/* Page Users — the clients of the platform: who they are, where they came from, how often they
   book and whether the account is still alive. The card of a client opens over the list */
class Users extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            limit: 30,
            page: 1,
            statistics: null,
            channels: [],
            ...INITIAL
        }

        this._user = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, kind, state, channel, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        list({ page, search, kind, state, channel, sort }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    channels: response.data.channels
                })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Every filter starts from the first page */
    refilter = fields => {
        this.setState(fields, () => this.load(1, true))
    }

    /* The search waits for the hand to stop */
    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(1, true), text.length === 0 ? 0 : 450)
    }

    /* A filter pressed twice lets go */
    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Пользователи</div>

                <div className="organization-header-tools">
                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(1, true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Имя, телефон или почта" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>
                </div>

            </div>
        )
    }

    /* The numbers of the platform — the kinds of accounts on one bar, the rest as tiles */
    _summary = () => {

        /* Fields */
        const { statistics, kind, state } = this.state

        if (!statistics) {
            return null
        }

        const parts = ["app", "point", "lead"].map(key => ({ key, value: statistics[key], ...KINDS[key] }))

        const tiles = [
            { key: "returning", icon: "refresh", value: statistics.returning, text: "возвращаются", hint: "2 и больше записей", accent: "green" },
            { key: "push", icon: "notification", value: statistics.push, text: "с push", hint: "есть устройство", accent: "blue" },
            { key: "deleted", icon: "trash", value: statistics.deleted, text: "удалили аккаунт", accent: "orange", filter: true },
            { key: "blocked", icon: "power", value: statistics.blocked, text: "заблокированы", accent: "red", filter: true }
        ]

        return (
            <div className="user-summary">

                <div className="user-summary-main">

                    <div className="user-summary-top">
                        <div>
                            <b>{statistics.total.toLocaleString("ru-RU")}</b>
                            <span>{plural(statistics.total, "клиент", "клиента", "клиентов")} на платформе</span>
                        </div>
                        <div className="user-summary-fresh">
                            <Icon name="trend" size={14} width={2.2} />
                            +{statistics.fresh.toLocaleString("ru-RU")} за 30 дней
                        </div>
                    </div>

                    <div className="user-summary-bar">
                        {parts.map(part =>
                            <i key={part.key} className={part.accent} style={{ flex: Math.max(part.value, 1) }} />
                        )}
                    </div>

                    <div className="user-summary-legend">
                        {parts.map(part =>
                            <div key={part.key} onClick={() => this.toggle("kind", part.key, "all")} className={`${part.accent} ${kind === part.key ? "active" : ""}`} title={part.hint}>
                                <i />
                                <span>{part.text}</span>
                                <b>{part.value.toLocaleString("ru-RU")}</b>
                            </div>
                        )}
                    </div>

                </div>

                <div className="user-summary-tiles">
                    {tiles.map(tile =>
                        <div
                            key={tile.key}
                            onClick={() => tile.filter && this.toggle("state", tile.key, "all")}
                            className={`user-tile ${tile.accent} ${tile.filter ? "clickable" : ""} ${state === tile.key ? "active" : ""}`}
                            title={tile.hint ? tile.hint : ""}
                        >
                            <div className="user-tile-icon"><Icon name={tile.icon} size={16} width={2} /></div>
                            <b>{tile.value.toLocaleString("ru-RU")}</b>
                            <span>{tile.text}</span>
                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* Where the clients book from — each channel filters the list by itself */
    _channels = () => {

        /* Fields */
        const { channels, channel } = this.state

        if (channels.length === 0) {
            return null
        }

        const total = channels.reduce((sum, item) => sum + item.count, 0)

        return (
            <div className="user-channels-strip">
                <div className="user-channels-strip-title">Откуда записываются</div>
                <div className="user-channels-strip-list">
                    {channels.map(item => {

                        const meta = channelOf(item.channel)

                        return (
                            <div
                                key={item.channel}
                                onClick={() => this.toggle("channel", item.channel, "")}
                                className={`user-strip-channel ${channel === item.channel ? "active" : ""}`}
                                style={{ "--channel": meta.color }}
                                title={`${item.count} записей от ${item.users} ${plural(item.users, "клиента", "клиентов", "клиентов")}`}
                            >
                                <i style={{ width: `${Math.max(6, item.count / total * 100)}%` }} />
                                <span>{meta.text}</span>
                                <b>{Math.round(item.count / total * 100)}%</b>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { state, sort, count } = this.state

        const states = [
            { key: "all", text: "Все" },
            { key: "active", text: "Активные" },
            { key: "deleted", text: "Удалённые" },
            { key: "blocked", text: "Заблокированные" }
        ]

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {states.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ state: item.key })} className={state === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

                <div className="user-count">{count.toLocaleString("ru-RU")} {plural(count, "клиент", "клиента", "клиентов")}</div>

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    {SORTS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

            </div>
        )
    }

    /* One client of the list */
    _item = item => {

        const kind = KINDS[item.kind] ? KINDS[item.kind] : KINDS.lead
        const state = stateOf(item)
        const image = item.image ? `${env.mediapoint}users/${item.image}` : null
        const name = item.name && `${item.name}`.trim().length > 0 ? `${item.name}`.trim() : null
        const first = item.firstChannel ? channelOf(item.firstChannel) : null
        const done = item.bookings > 0 ? item.completed / item.bookings * 100 : 0
        const missed = item.bookings > 0 ? item.missed / item.bookings * 100 : 0

        return (
            <div key={`${item.id}`} onClick={() => this._user.current.open(item)} className={`user-item ${state.key}`}>

                <div className="user-item-cell person">
                    <div className="user-item-avatar-wrap" style={{ "--avatar": avatarColorOf(item.id) }}>
                        <Avatar className="user-item-avatar" src={image} alt={name}>
                            {name ? <span>{initialsOf(name)}</span> : <Icon name="user" size={22} width={2} />}
                        </Avatar>
                        {item.push && <div className="user-item-push" title="Есть push"><Icon name="notification" size={9} width={2.6} /></div>}
                    </div>
                    <div className="user-item-title">
                        <div className={`user-item-name ${name ? "" : "empty"}`}>{name ? name : "Без имени"}</div>
                        <div className="user-item-phone">{item.phone ? item.phone : item.email}</div>
                    </div>
                </div>

                <div className="user-item-cell kind">
                    <div className={`user-badge ${kind.accent}`} title={kind.hint}><Icon name={kind.icon} size={12} width={2.2} />{kind.text}</div>
                </div>

                <div className="user-item-cell visits">
                    {item.bookings > 0
                        ? <div className="user-item-visits">
                            <b>{item.bookings}</b>
                            <div className="user-item-visits-bar" title={`Состоялись: ${item.completed}, отмены и неявки: ${item.missed}`}>
                                <i className="done" style={{ width: `${done}%` }} />
                                <i className="missed" style={{ width: `${missed}%` }} />
                            </div>
                            <span>{item.organizations} {plural(item.organizations, "точка", "точки", "точек")}</span>
                        </div>
                        : <div className="user-item-muted">Нет записей</div>
                    }
                </div>

                <div className="user-item-cell last">
                    {item.lastVisit
                        ? <>
                            <b>{ago(item.lastVisit)}</b>
                            <span>{item.lastOrganization}</span>
                        </>
                        : <div className="user-item-muted">—</div>
                    }
                </div>

                <div className="user-item-cell channel">
                    {first
                        ? <div className="user-item-channel" style={{ "--channel": first.color }} title="Первая запись"><i />{first.text}</div>
                        : <div className="user-item-muted">—</div>
                    }
                </div>

                <div className="user-item-cell state">
                    <div className={`organization-item-state ${state.key === "active" ? "on" : "off"}`}>
                        <i />
                        {state.key === "active" ? ago(item.createdAt) : state.text}
                    </div>
                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data, count, page, limit } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._summary()}
                    {this._channels()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ ...INITIAL })} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._summary()}
                {this._channels()}
                {this._toolbar()}

                <div className="service-list">

                    <div className="user-item head">
                        <div className="user-item-cell person">Клиент</div>
                        <div className="user-item-cell kind">Аккаунт</div>
                        <div className="user-item-cell visits">Записи</div>
                        <div className="user-item-cell last">Последний визит</div>
                        <div className="user-item-cell channel">Первая запись</div>
                        <div className="user-item-cell state">В базе</div>
                    </div>

                    {data.map(item => this._item(item))}

                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    render = () => {
        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <UserModal ref={this._user} onChanged={() => this.load(this.state.page)} />

            </div>
        )
    }
}

export default Users
